import { BadgeCheck, Ban, Check, ChevronRight, Clock, FileWarning, PauseCircle, Send } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { KYC_ENABLED } from '@sense/shared';
import { api } from '../lib/api';
import { useSession } from '../lib/session';
import { toast } from '../lib/toast';
import { Button, Card, IconTile } from './ui';
import { tr } from '../lib/i18n';

const KYC = ['shop_photo', 'owner_selfie', 'id_proof', 'address_proof'];

/** Billing is open while a store is approved or paused (paused only stops online orders). */
export function useOperational(): boolean {
  const status = useSession((s) => s.me?.store.status);
  return !status || status === 'approved' || status === 'paused';
}

/** What the owner still has to do before the platform team can review the store. */
export function useReviewChecklist() {
  const me = useSession((s) => s.me);
  const steps = me?.store.setup_steps ?? [];
  const docs = me?.store.kyc_docs ?? [];
  const items = [
    { id: 'details', label: tr('Store details'), done: steps.includes('details') },
    { id: 'business', label: tr('Business info (PAN, GSTIN)'), done: steps.includes('business') },
    ...(KYC_ENABLED ? [{ id: 'kyc', label: `KYC documents · ${docs.filter((d) => KYC.includes(d)).length} of 4`, done: KYC.every((d) => docs.includes(d)) }] : []),
  ];
  return { items, ready: items.every((i) => i.done) };
}

/** Shown instead of billing until the store is approved; the owner can finish setup and send it for review. */
export function ApprovalGate() {
  const me = useSession((s) => s.me);
  const loadMe = useSession((s) => s.loadMe);
  const { items, ready } = useReviewChecklist();
  const [busy, setBusy] = useState(false);
  if (!me) return null;
  const st = me.store.status;
  const owner = me.user.role === 'owner';
  const submitted = !!me.store.submitted_at && st === 'pending';

  const submit = async () => {
    setBusy(true);
    try {
      const r = await api<{ status: string }>('/api/store/submit', { method: 'POST', json: {} });
      await loadMe();
      toast.ok(r.status === 'approved' ? tr('Approved — billing is open') : tr('Sent for review. We’ll let you know.'));
    } catch (e) { toast.error((e as Error).message); } finally { setBusy(false); }
  };

  const head = st === 'rejected' ? { icon: FileWarning, tint: 'red' as const, title: tr('Changes needed before approval'), text: me.store.review_note ?? 'Please check your details, then send them again.' }
    : st === 'deactivated' ? { icon: Ban, tint: 'red' as const, title: tr('This store is closed on Sense Daily'), text: me.store.review_note ?? 'Contact support to reopen it.' }
      : submitted ? { icon: Clock, tint: 'amber' as const, title: tr('Your store is being reviewed'), text: 'We check your store details and open billing, usually within one working day. You can set up products and staff meanwhile.' }
        : { icon: BadgeCheck, tint: 'green' as const, title: tr('Finish setup to open billing'), text: 'Every store is checked once before it can bill. Complete these steps and send them for review.' };

  return (
    <div className="mx-auto flex max-w-xl flex-col items-center px-4 py-10 text-center">
      <IconTile icon={head.icon} tint={head.tint} size={72} />
      <h1 className="mt-4 text-2xl font-bold">{head.title}</h1>
      <p className="mt-2 text-muted">{head.text}</p>
      {owner && st !== 'deactivated' && (
        <Card className="mt-6 w-full p-2 text-left">
          {items.map((i) => (
            <Link key={i.id} to="/setup" className="flex items-center gap-3 rounded-2xl px-3 py-3 hover:bg-forest-50">
              <span className={`flex h-8 w-8 items-center justify-center rounded-full ${i.done ? 'bg-forest-700 text-white' : 'border-2 border-line text-transparent'}`}><Check size={16} /></span>
              <span className="flex-1 font-bold">{i.label}</span><ChevronRight size={18} className="text-muted" />
            </Link>
          ))}
        </Card>
      )}
      {owner && (st === 'pending' || st === 'rejected') && (
        <div className="mt-5 flex w-full flex-col gap-2 sm:flex-row sm:justify-center">
          <Link to="/setup" className="inline-flex h-12 items-center justify-center rounded-full border border-line bg-white px-5 font-bold">{tr('Open setup')}</Link>
          <Button size="lg" disabled={!ready || busy || (submitted && st === 'pending')} onClick={() => void submit()}><Send size={18} />{submitted && st === 'pending' ? tr('Sent for review') : busy ? tr('Sending…') : tr('Send for review')}</Button>
        </div>
      )}
      <button onClick={() => void loadMe()} className="mt-4 text-sm font-bold text-primary">{tr('Check again')}</button>
    </div>
  );
}

/** Thin banner for the owner while the store is not fully live. */
export function StatusBanner() {
  const me = useSession((s) => s.me);
  if (!me || me.user.role !== 'owner' || !me.store.status || me.store.status === 'approved') return null;
  const st = me.store.status;
  const text = st === 'paused' ? 'Paused by the platform: billing works, online orders are off.'
    : st === 'pending' ? (me.store.submitted_at ? 'Under review — billing opens once approved.' : 'Finish setup and send it for review to open billing.')
      : st === 'rejected' ? `Changes needed: ${me.store.review_note ?? 'see setup'}` : 'This store is closed on Sense Daily.';
  return (
    <Link to={st === 'paused' ? '/' : '/billing'} className={`flex items-center gap-2 px-4 py-2 text-sm font-bold lg:px-8 ${st === 'paused' ? 'bg-t-blue text-t-blue-fg' : st === 'pending' ? 'bg-t-amber text-t-amber-fg' : 'bg-t-red text-danger'}`}>
      {st === 'paused' ? <PauseCircle size={16} /> : <Clock size={16} />}<span className="flex-1">{text}</span><ChevronRight size={16} />
    </Link>
  );
}
